import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loading, PageHeader, Stat } from '../App';
import { ErrorState } from '../components/States';
import { adminApi, classifyError, dateTime, money, type AdminDashboard, type ApiError } from '../api/admin';

/** The API returns the enum name; these are what an administrator should read. */
const CATEGORY_LABELS: Record<string, string> = {
  PgHostel: 'PG / Hostel',
  Hotel: 'Hotel',
  HomeRental: 'Home rental',
};

export function DashboardPage() {
  const [data, setData] = useState<AdminDashboard | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    setError(null);

    try {
      setData(await adminApi.dashboard());
    } catch (err) {
      setError(classifyError(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!data) return <Loading rows={8} />;

  return (
    <>
      <PageHeader
        title="Platform overview"
        description="Everything below reflects what is currently in the database."
      />

      {data.simulatedPaymentCount > 0 && (
        <div className="admin-simulated mb-3">
          <strong>{data.simulatedPaymentCount} simulated payment{data.simulatedPaymentCount === 1 ? '' : 's'}</strong>{' '}
          exist in this database, created by the development payment gateway. They are excluded from every revenue
          figure on this page, which is why collections can read zero while payments exist.
        </div>
      )}

      <section className="mb-4">
        <h2 className="h6 text-uppercase text-muted mb-2">Needs attention</h2>
        <div className="row g-2">
          <div className="col-6 col-lg-3">
            <Link to="/moderation?status=PendingReview" className="text-decoration-none">
              <Stat value={data.pendingVerification} label="Awaiting review" tone={data.pendingVerification > 0 ? 'warn' : undefined} />
            </Link>
          </div>
          <div className="col-6 col-lg-3">
            <Link to="/reports" className="text-decoration-none">
              <Stat value={data.openReports} label="Open reports" tone={data.openReports > 0 ? 'warn' : undefined} />
            </Link>
          </div>
          <div className="col-6 col-lg-3">
            <Stat
              value={data.subscriptionsExpiringIn7Days}
              label="Subscriptions expiring in 7 days"
              tone={data.subscriptionsExpiringIn7Days > 0 ? 'warn' : undefined}
            />
          </div>
          <div className="col-6 col-lg-3">
            <Stat value={data.failedPayments} label="Failed payments" />
          </div>
        </div>
      </section>

      <section className="mb-4">
        <h2 className="h6 text-uppercase text-muted mb-2">Accounts</h2>
        <div className="row g-2">
          <div className="col-6 col-lg-3">
            <Stat value={data.totalUsers} label="Total users" />
          </div>
          <div className="col-6 col-lg-3">
            <Stat value={data.totalOwners} label="Property owners" />
          </div>
          <div className="col-6 col-lg-3">
            <Stat value={data.totalCustomers} label="Customers" />
          </div>
          <div className="col-6 col-lg-3">
            <Stat value={data.newUsersLast30Days} label="New in 30 days" />
          </div>
        </div>
      </section>

      <section className="mb-4">
        <h2 className="h6 text-uppercase text-muted mb-2">Listings</h2>
        <div className="row g-2">
          <div className="col-6 col-lg-2">
            <Stat value={data.totalBusinesses} label="Businesses" />
          </div>
          <div className="col-6 col-lg-2">
            <Stat value={data.pgBranches} label="PG branches" />
          </div>
          <div className="col-6 col-lg-2">
            <Stat value={data.homeListings} label="Home listings" />
          </div>
          <div className="col-6 col-lg-2">
            <Stat value={data.hotelBranches} label="Hotel branches" />
          </div>
          <div className="col-6 col-lg-2">
            <Stat value={data.activeListings} label="Live now" tone="ok" />
          </div>
          <div className="col-6 col-lg-2">
            <Stat value={data.expiredListings} label="Expired" />
          </div>
        </div>
      </section>

      <section className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="h6 text-uppercase text-muted mb-0">How listings got live</h2>
          <Link to="/settings/payments" className="small">
            Payment configuration
          </Link>
        </div>

        <div className="row g-2">
          <div className="col-6 col-lg-3">
            <Stat value={data.paidSubscriptions} label="Paid subscriptions" />
          </div>
          <div className="col-6 col-lg-3">
            <Stat value={data.freeActivations} label="Free / complimentary activations" />
          </div>
          <div className="col-6 col-lg-3">
            <Stat value={data.paidHomeListings} label="Paid home listings" />
          </div>
          <div className="col-6 col-lg-3">
            <Stat
              value={data.pendingPaymentRecords}
              label="Payments started, not confirmed"
              tone={data.pendingPaymentRecords > 0 ? 'warn' : undefined}
            />
          </div>
        </div>

        <p className="small text-muted mt-2 mb-1">
          A free activation is a listing that is live without any captured payment, because payment is switched off for
          its category. It is counted here and nowhere in the money figures below.
          {data.simulatedPaymentCount > 0 && (
            <>
              {' '}
              A listing backed only by a development-gateway payment is neither, so these two counts will not add up to
              the number of live listings while simulated payments exist.
            </>
          )}
        </p>

        <ul className="small text-muted mb-0">
          {data.paymentConfiguration.map((category) => (
            <li key={category.category}>
              {CATEGORY_LABELS[category.category] ?? category.category}:{' '}
              {category.paymentEnabled ? (
                <>payment required, {money(category.amount)} plus tax</>
              ) : (
                <>payment not required</>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="h6 text-uppercase text-muted mb-0">Multi-branch organisations</h2>
          <Link to="/settings/payments" className="small">
            Configure
          </Link>
        </div>

        <div className="row g-2">
          <div className="col-6 col-lg-3">
            <Stat value={data.multiBranchOrganisations} label="Entitled to several branches" />
          </div>
          <div className="col-6 col-lg-3">
            <Stat value={data.multiBranchFreeGrants} label="Waived (not revenue)" />
          </div>
          <div className="col-6 col-lg-3">
            <Stat
              value={data.multiBranchEnabled ? 'On' : 'Off'}
              label="Multi-branch feature"
              tone={data.multiBranchEnabled ? 'ok' : undefined}
            />
          </div>
          <div className="col-6 col-lg-3">
            <Stat
              value={data.multiBranchPaymentRequired ? 'Charged' : 'Free'}
              label="Yearly subscription"
            />
          </div>
        </div>

        <p className="small text-muted mt-2 mb-0">
          A waived entitlement was granted because payment is switched off for it. It is counted here and nowhere in
          the money figures below.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h6 text-uppercase text-muted mb-2">Collections</h2>

        <div className="row g-2">
          <div className="col-6 col-lg-3">
            <Stat value={money(data.grossCollections)} label="Gross collected (incl. tax)" />
          </div>
          <div className="col-6 col-lg-3">
            <Stat value={money(data.netRevenue)} label="Net revenue (after tax & refunds)" />
          </div>
          <div className="col-6 col-lg-3">
            <Stat value={data.successfulPayments} label="Successful payments" />
          </div>
          <div className="col-6 col-lg-3">
            <Stat value={data.activeSubscriptions} label="Active subscriptions" />
          </div>
        </div>

        <p className="small text-muted mt-2 mb-0">
          Money actually collected. Free activations contribute nothing to these figures. Renewals due are shown as a
          count on the financial report, never as forecast revenue.
        </p>
      </section>

      <section>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="h6 text-uppercase text-muted mb-0">Recent activity</h2>
          <Link to="/audit" className="small">
            Full audit log
          </Link>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Action</th>
                <th scope="col">Entity</th>
                <th scope="col">Actor</th>
                <th scope="col">Note</th>
              </tr>
            </thead>
            <tbody>
              {data.recentActivity.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-muted">
                    Nothing recorded yet.
                  </td>
                </tr>
              ) : (
                data.recentActivity.map((entry, index) => (
                  <tr key={`${entry.atUtc}-${index}`}>
                    <td className="text-nowrap">{dateTime(entry.atUtc)}</td>
                    <td>
                      <code className="small">{entry.action}</code>
                    </td>
                    <td>{entry.entityType}</td>
                    <td className="text-muted">{entry.actor ?? '—'}</td>
                    <td className="text-muted">{entry.summary ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
